import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Autocomplete,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TablePagination,
    TableSortLabel,
    Tooltip,
    Snackbar,
    Alert,
    Chip
} from '@mui/material';
import {
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Link as LinkIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { formatarCPF } from '../../utils/formatadores';
import Preloader from '../../components/Preloader';

export default function AtribuirRepresentante() {
    const { user } = useAuth();
    const [beneficiarios, setBeneficiarios] = useState([]);
    const [representantes, setRepresentantes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Filtros
    const [filtros, setFiltros] = useState({
        st_nome: "",
        st_cpf: "",
    });

    // Ordenação e Paginação
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("st_nome");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Edição
    const [editingBeneficiario, setEditingBeneficiario] = useState(null);
    const [selectedRep, setSelectedRep] = useState(null);

    useEffect(() => {
        carregarDados();
    }, []);

    const carregarDados = async () => {
        setLoading(true);
        try {
            const [resBeneficiarios, resReps] = await Promise.all([
                api.get('/beneficiario'),
                api.get('/representante')
            ]);

            if (resBeneficiarios.data.status === 'sucesso') {
                setBeneficiarios(resBeneficiarios.data.dados);
            }
            if (resReps.data.status === 'sucesso') {
                setRepresentantes(resReps.data.dados);
            }
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setError('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltroChange = (event) => {
        const { name, value } = event.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleEdit = (beneficiario) => {
        setEditingBeneficiario(beneficiario);
        const currentRep = representantes.find(r => r.id_representante === beneficiario.id_representante);
        setSelectedRep(currentRep || null);
    };

    const handleSave = async () => {
        if (!editingBeneficiario) return;

        try {
            await api.put(`/beneficiario/${editingBeneficiario.id_beneficiario}`, {
                id_representante: selectedRep ? selectedRep.id_representante : null
            });

            setBeneficiarios(prev => prev.map(c =>
                c.id_beneficiario === editingBeneficiario.id_beneficiario
                    ? { ...c, id_representante: selectedRep?.id_representante, representante: selectedRep }
                    : c
            ));

            setSuccess('Representante atualizado com sucesso!');
            handleClose();
        } catch (error) {
            console.error('Erro ao salvar:', error);
            setError('Erro ao atualizar representante.');
        }
    };

    const handleClose = () => {
        setEditingBeneficiario(null);
        setSelectedRep(null);
    };

    // Filtragem e Ordenação
    const beneficiariosFiltrados = beneficiarios
        .filter((beneficiario) => {
            const nomeMatch = !filtros.st_nome ||
                beneficiario.usuario?.st_nome?.toLowerCase().includes(filtros.st_nome.toLowerCase());
            const cpfMatch = !filtros.st_cpf ||
                beneficiario.st_cpf?.includes(filtros.st_cpf.replace(/\D/g, ''));

            return nomeMatch && cpfMatch;
        })
        .sort((a, b) => {
            let aValue = "";
            let bValue = "";

            if (orderBy === 'representative_name') {
                aValue = a.representante?.st_nome || "";
                bValue = b.representante?.st_nome || "";
            } else if (orderBy === 'st_nome') {
                aValue = a.usuario?.st_nome || "";
                bValue = b.usuario?.st_nome || "";
            } else if (orderBy === 'st_telefone') {
                aValue = a.usuario?.st_telefone || "";
                bValue = b.usuario?.st_telefone || "";
            } else {
                aValue = a[orderBy] || "";
                bValue = b[orderBy] || "";
            }

            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (order === "asc") {
                return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
            } else {
                return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
            }
        });

    return (
        <Box sx={{ p: { xs: 2, sm: 3 }, height: '100vh', overflow: 'auto' }}>
            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>

            <Snackbar
                open={!!success}
                autoHideDuration={6000}
                onClose={() => setSuccess(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
                    {success}
                </Alert>
            </Snackbar>

            <Box sx={{
                margin: '0 1.5% 1.5% 1.5%',
                height: '90vh',
                display: 'flex',
                flexDirection: 'column'
            }}>
                {/* Header */}
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: { xs: 2, sm: 3 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                }}>
                    <Typography variant="h5" sx={{
                        fontSize: { xs: '1.5rem', sm: '2rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <LinkIcon sx={{ fontSize: 'inherit', color: '#20ac9c' }} />
                        Atribuição de Representante
                    </Typography>
                </Box>

                {/* Tabela */}
                <TableContainer component={Paper} sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'auto',
                    height: { xs: 'calc(90vh - 140px)', sm: 'calc(90vh - 120px)' },
                    maxHeight: { xs: 'calc(90vh - 140px)', sm: 'calc(90vh - 120px)' },
                    width: '100%',
                    boxShadow: 2,
                    borderRadius: 2
                }}>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '30%' }}>
                                    <TableSortLabel
                                        active={orderBy === "st_nome"}
                                        direction={orderBy === "st_nome" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_nome")}
                                    >
                                        Beneficiário
                                    </TableSortLabel>
                                    <TextField
                                        name="st_nome"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_nome}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar por nome"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '20%' }}>
                                    <TableSortLabel
                                        active={orderBy === "st_cpf"}
                                        direction={orderBy === "st_cpf" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_cpf")}
                                    >
                                        CPF
                                    </TableSortLabel>
                                    <TextField
                                        name="st_cpf"
                                        size="small"
                                        fullWidth
                                        value={filtros.st_cpf}
                                        onChange={handleFiltroChange}
                                        placeholder="Filtrar CPF"
                                        sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem' } }}
                                    />
                                </TableCell>
                                <TableCell sx={{ width: '15%' }}>
                                    <TableSortLabel
                                        active={orderBy === "st_telefone"}
                                        direction={orderBy === "st_telefone" ? order : "asc"}
                                        onClick={() => handleRequestSort("st_telefone")}
                                    >
                                        Telefone
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '25%' }}>
                                    <TableSortLabel
                                        active={orderBy === "representative_name"}
                                        direction={orderBy === "representative_name" ? order : "asc"}
                                        onClick={() => handleRequestSort("representative_name")}
                                    >
                                        Representante Atual
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ width: '10%', textAlign: 'center' }}>Ações</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Preloader message="Carregando dados..." />
                                    </TableCell>
                                </TableRow>
                            ) : beneficiariosFiltrados.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">Nenhum beneficiário encontrado</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                beneficiariosFiltrados
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((beneficiario, index) => (
                                        <TableRow key={beneficiario.id_beneficiario}
                                            sx={{
                                                backgroundColor: (theme) => index % 2 === 0
                                                    ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)')
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)'
                                                }
                                            }}
                                        >
                                            <TableCell>{beneficiario.usuario?.st_nome || 'N/A'}</TableCell>
                                            <TableCell>{formatarCPF(beneficiario.st_cpf)}</TableCell>
                                            <TableCell>{beneficiario.usuario?.st_telefone || 'N/A'}</TableCell>
                                            <TableCell>
                                                {beneficiario.representante ? (
                                                    <Chip
                                                        label={beneficiario.representante.st_nome}
                                                        color="primary"
                                                        variant="outlined"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Typography variant="button" color="text.secondary" fontSize="0.75rem">
                                                        NÃO ATRIBUÍDO
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="Alterar Representante">
                                                    <IconButton size="small" onClick={() => handleEdit(beneficiario)}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={beneficiariosFiltrados.length}
                    page={page}
                    onPageChange={(e, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    labelRowsPerPage="Itens por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    sx={{ backgroundColor: (theme) => theme.palette.background.default, borderTop: 'none', width: '100%', marginTop: 0 }}
                />
            </Box>

            {/* Dialog de Edição */}
            <Dialog open={!!editingBeneficiario} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: '#20ac9c', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Alterar Representante
                    <IconButton onClick={handleClose} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Beneficiário: <strong>{editingBeneficiario?.usuario?.st_nome}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            CPF: {formatarCPF(editingBeneficiario?.st_cpf)}
                        </Typography>

                        <Autocomplete
                            options={representantes}
                            getOptionLabel={(option) => option.st_nome}
                            value={selectedRep}
                            onChange={(event, newValue) => {
                                setSelectedRep(newValue);
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Selecione o Representante"
                                    placeholder="Buscar por nome..."
                                    variant="outlined"
                                    fullWidth
                                />
                            )}
                            noOptionsText="Nenhum representante encontrado"
                            sx={{ mt: 3 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose} startIcon={<CancelIcon />}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ bgcolor: '#20ac9c', '&:hover': { bgcolor: '#167a6f' } }} startIcon={<SaveIcon />}>
                        Salvar Alteração
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
